import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import { EligibilityYesAndNo } from "@/blocks/EligibilityYesAndNo";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import { handlePossessiveApostrophe } from "@/utils/string";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

export const EligibilityLiveCareChildrenScreen = () => {
  const router = useRouter();

  const { supporterFirstName, supporterHouseholdMemberId } =
    useLocalSearchParams<{
      supporterFirstName: string;
      supporterHouseholdMemberId: string;
    }>();

  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);

  const handleYesPress = () => setIsAddingNewHouseholdMember(true);

  if (!currentHouseholdMember?.householdId) {
    return <VnLoadingSpinner className="mt-2" />;
  }

  const goToNextStep = () =>
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "LIVE_CARE_CHILDREN",
        },
      },
      {
        onSuccess: () => {
          router.navigate({
            pathname: "/eligibility/parent-guardian-student",
            params: {
              supporterFirstName,
              supporterHouseholdMemberId,
            },
          });
        },
      },
    );

  if (isAddingNewHouseholdMember) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={[
          "CHILD",
          "STEPCHILD",
          "GRANDCHILD",
          "FOSTER_CHILD",
          "OTHER",
        ]}
        ageRangeOptions={["BABY", "CHILD", "YOUTH", "YOUNG_ADULT"]}
        assumptions={{
          taxFilingStatus: "NON_FILER",
          maritalStatus: "NEVER_MARRIED",
          childCaregiverStatus: "NOT_CAREGIVER",
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: supporterHouseholdMemberId
              ? "DEPENDENT_OF_SUPPORTER"
              : "SUPPORT_OF",
          },
          sameMailingAddress: true,
        }}
        titleI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityLiveAndCareScreen.supporterFormTitle"
            : "native:eligibilityLiveAndCareScreen.formTitle"
        }
        descriptionI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilityLiveAndCareScreen.supporterFormDescription"
            : "native:eligibilityLiveAndCareScreen.formDescription"
        }
        titleI18nOptions={{
          supporterFirstName: handlePossessiveApostrophe(supporterFirstName),
        }}
        descriptionI18nOptions={{
          supporterFirstName: supporterFirstName,
        }}
        onSuccessOrContinue={goToNextStep}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  return (
    <EligibilityYesAndNo
      titleI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilityLiveAndCareScreen.supporterTitle"
          : "native:eligibilityLiveAndCareScreen.title"
      }
      titleI18nOptions={{
        supporterFirstName: supporterFirstName,
      }}
      descriptionI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilityLiveAndCareScreen.supporterDescription"
          : "native:eligibilityLiveAndCareScreen.description"
      }
      descriptionI18nOptions={{
        supporterFirstName: supporterFirstName,
      }}
      onNoPress={goToNextStep}
      isNoLoading={isUpdatingHousehold}
      onYesPress={handleYesPress}
    />
  );
};
